import React from "react";
import { Badge, Empty, Spin, Tag, Typography } from "antd";
import dayjs from "dayjs";

import { CHANNELS, CHAT_STATUS_COLOR } from "./constants";

const { Text } = Typography;

/**
 * The left pane. Driven by the unified conversation envelope, so the same list
 * renders WhatsApp numbers and email addresses.
 */

const displayNameOf = (conversation) =>
  conversation.counterparty?.display_name
  || conversation.counterparty?.address
  || conversation.visitor?.name
  || conversation.visitor?.email
  || (conversation.channel === CHANNELS.CHAT ? "Website visitor" : "");

const avatarFor = (conversation) =>
  `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
    displayNameOf(conversation) || "?"
  )}`;

/** Website chat rows carry session state the other channels do not have. */
const ChatExtras = ({ conversation }) => {
  const { status, assigned_to_name: assignee, ticket_number: ticket } = conversation;
  const email = conversation.visitor?.email;
  const name = displayNameOf(conversation);
  if (!status && !assignee && !ticket && !(email && email !== name)) return null;
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 4,
        marginTop: 3,
      }}
    >
      {status ? (
        <Tag
          color={CHAT_STATUS_COLOR[status] || "default"}
          style={{ fontSize: 10, lineHeight: "16px", margin: 0, textTransform: "capitalize" }}
        >
          {status}
        </Tag>
      ) : null}
      {ticket ? (
        <Tag color="purple" style={{ fontSize: 10, lineHeight: "16px", margin: 0 }}>
          #{ticket}
        </Tag>
      ) : null}
      {assignee ? (
        <Text type="secondary" ellipsis style={{ fontSize: 10.5, maxWidth: 120 }}>
          @{assignee}
        </Text>
      ) : null}
      {email && email !== name ? (
        <Text type="secondary" ellipsis style={{ fontSize: 10.5, maxWidth: 150 }}>
          {email}
        </Text>
      ) : null}
    </div>
  );
};

const timeLabel = (value) => {
  if (!value) return "";
  const date = dayjs(value);
  const today = dayjs();
  if (date.isSame(today, "day")) return date.format("HH:mm");
  if (date.isSame(today.subtract(1, "day"), "day")) return "Yesterday";
  return date.format("DD/MM/YY");
};

const ConversationList = ({
  conversations,
  loading,
  selectedId,
  onSelect,
  accentColor,
  showSource = false,
  emptyText = "No conversations yet",
}) => {
  if (loading && !conversations.length) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin />
      </div>
    );
  }
  if (!conversations.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={emptyText}
        style={{ marginTop: 48 }}
      />
    );
  }

  return conversations.map((conversation) => {
    const selected = conversation.id === selectedId;
    const name = displayNameOf(conversation);

    return (
      <div
        key={conversation.id}
        onClick={() => onSelect(conversation)}
        style={{
          display: "flex",
          gap: 10,
          padding: "10px 12px",
          cursor: "pointer",
          borderBottom: "1px solid #f0f2f5",
          background: selected ? "#f0f2f5" : "transparent",
        }}
      >
        <img
          src={avatarFor(conversation)}
          alt=""
          style={{ width: 42, height: 42, borderRadius: "50%", flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <Text
              strong={!conversation.is_read}
              ellipsis
              style={{ fontSize: 14, flex: 1 }}
            >
              {name}
            </Text>
            <Text
              style={{
                fontSize: 11,
                color: conversation.unread_count ? accentColor : "#667781",
                whiteSpace: "nowrap",
              }}
            >
              {timeLabel(conversation.last_message_at)}
            </Text>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <Text
              type="secondary"
              ellipsis
              style={{ fontSize: 12.5, flex: 1 }}
            >
              {conversation.last_message_preview || "—"}
            </Text>
            {conversation.unread_count ? (
              <Badge
                count={conversation.unread_count}
                style={{ backgroundColor: accentColor }}
              />
            ) : null}
          </div>
          {/* Which number or mailbox this chat came through — only worth the
              row when the list spans more than one. */}
          {showSource && conversation.source?.address ? (
            <Text type="secondary" style={{ fontSize: 10.5 }}>
              {conversation.source.address}
            </Text>
          ) : null}
          {conversation.channel === CHANNELS.CHAT
            || String(conversation.id || "").startsWith("ch:") ? (
            <ChatExtras conversation={conversation} />
          ) : null}
        </div>
      </div>
    );
  });
};

export default ConversationList;
